import { type ReactNode } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'

import { Button } from '@/components/Button'
import { ModalDialogWizardNavContent } from '@/components/Wizard/WizardNav'

export interface TaxInfoGatheringNavigationModalProps {
  children?: ReactNode
  isOpen: boolean
  onClose?: () => unknown
  completionPercentage: number
  onConnectWithProPress?: () => unknown
  testId?: string
  connectWithProButtonTestId?: string
  closeButtonTestId?: string
}

/**
 * Info gathering navigation modal.
 * The wizard nav list should be using the `flat` variant.
 */
export function TaxInfoGatheringNavigationModal({
  children,
  isOpen,
  onClose,
  completionPercentage,
  onConnectWithProPress,
  testId,
  connectWithProButtonTestId,
  closeButtonTestId,
}: TaxInfoGatheringNavigationModalProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      sheetSizing="stretch"
      testId={testId}
    >
      <ModalDialogHeader textAlign="left">
        <ModalDialogTitle>Your checklist</ModalDialogTitle>
        <ModalDialogDescription>
          You've completed{' '}
          <span className="text-strong">
            {Math.floor(completionPercentage)}%
          </span>{' '}
          of your checklist.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogWizardNavContent spacingY="gapless" spacingX="flat">
        {children}
      </ModalDialogWizardNavContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            variant="outline"
            label="Connect with a Pro"
            onPress={onConnectWithProPress}
            testId={connectWithProButtonTestId}
          />
          <Button
            variant="text"
            label="Close"
            onPress={onClose}
            testId={closeButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

export interface TaxInfoGatheringMissingRequiredItemsNavigationModalProps {
  children?: ReactNode
  isOpen: boolean
  onClose?: () => unknown
  testId?: string
  closeButtonTestId?: string
}

/**
 * Modal for displaying missing required items via a navigation menu.
 * The wizard nav list should be using the `flat` variant.
 */
export function TaxInfoGatheringMissingRequiredItemsNavigationModal({
  children,
  isOpen,
  onClose,
  testId,
  closeButtonTestId,
}: TaxInfoGatheringMissingRequiredItemsNavigationModalProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      sheetSizing="stretch"
      testId={testId}
    >
      <ModalDialogHeader textAlign="left">
        <ModalDialogTitle>Just a few more things</ModalDialogTitle>
        <ModalDialogDescription>
          Before we can connect you with a Pro, you'll need to complete the
          following items.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogWizardNavContent spacingY="gapless" spacingX="flat">
        {children}
      </ModalDialogWizardNavContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            variant="outline"
            label="Close"
            onPress={onClose}
            testId={closeButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
